import type { JSX } from "react";
import { secondsPerHour, secondsPerMinute } from "../../domain/time";
import type { LocalDate } from "../../domain/time";
import { useI18n } from "../../shared/i18n/I18nContext";
import type { Translate } from "../../shared/i18n/translate";
import { Alert } from "../../shared/ui/Alert";
import type { AlertTone } from "../../shared/ui/Alert";
import { formatLocalDate, formatTimeOrDateTime } from "../../shared/ui/format";
import styles from "./ClockPage.module.css";
import type { ClockWarning } from "./model/clockWarnings";

/**
 * Formats hours with up to one decimal (`6`, `8,5`).
 *
 * @param seconds - Duration.
 * @param languageTag - BCP 47 tag.
 * @returns Hours.
 */
function hours(seconds: number, languageTag: string): string {
  const formatted = new Intl.NumberFormat(languageTag, { maximumFractionDigits: 1 }).format(
    seconds / secondsPerHour,
  );

  return formatted;
}

/**
 * Rounds a remaining duration up to whole minutes (never shows 0 min before the limit).
 *
 * @param seconds - Duration.
 * @returns Minutes.
 */
function minutesUp(seconds: number): number {
  const minutes = Math.ceil(seconds / secondsPerMinute);

  return minutes;
}

/**
 * Describes a warning.
 *
 * @param warning - The warning.
 * @param t - Translation function.
 * @param languageTag - BCP 47 tag.
 * @param timeZone - Time zone for times.
 * @param today - Local date of today in `timeZone`; a time of another day names its date (F-17).
 * @param continuousLimit - Maximum continuous work in seconds.
 * @returns Tone and text.
 */
export function describeWarning(
  warning: ClockWarning,
  t: Translate,
  languageTag: string,
  timeZone: string,
  today: LocalDate,
  continuousLimit: number,
): { tone: AlertTone; text: string } {
  let described: { tone: AlertTone; text: string };

  switch (warning.kind) {
    case "breakDue":
      described = {
        tone: "warning",
        text: t("clock.warning.breakDue", {
          minutes: minutesUp(warning.inSeconds),
          limit: hours(continuousLimit, languageTag),
        }),
      };
      break;
    case "breakOverdue":
      described = {
        tone: "danger",
        text: t("clock.warning.breakOverdue", { limit: hours(continuousLimit, languageTag) }),
      };
      break;
    case "breakMissing":
      described = {
        tone: "warning",
        text: t("clock.warning.breakMissing", {
          required: Math.floor(warning.requiredSeconds / secondsPerMinute),
          taken: Math.floor(warning.takenSeconds / secondsPerMinute),
        }),
      };
      break;
    case "dailyLimitSoon":
      described = {
        tone: "warning",
        text: t("clock.warning.dailyLimitSoon", {
          minutes: minutesUp(warning.inSeconds),
          limit: hours(warning.limitSeconds, languageTag),
        }),
      };
      break;
    case "dailyLimitExceeded":
      described = {
        tone: "danger",
        text: t("clock.warning.dailyLimitExceeded", {
          limit: hours(warning.limitSeconds, languageTag),
        }),
      };
      break;
    case "regularLimitExceeded":
      described = {
        tone: "info",
        text: t("clock.warning.regularLimitExceeded", {
          limit: hours(warning.limitSeconds, languageTag),
        }),
      };
      break;
    case "restActive":
      described = {
        tone: "warning",
        text: t("clock.warning.restActive", {
          time: formatTimeOrDateTime(warning.until, today, languageTag, timeZone),
        }),
      };
      break;
    case "openDays":
      described = {
        tone: "warning",
        text: t("clock.warning.openDays", {
          dates: warning.dates.map((date) => formatLocalDate(date, languageTag)).join(", "),
        }),
      };
      break;
  }

  return described;
}

/** Properties of {@link WarningList}. */
export interface WarningListProps {
  readonly warnings: readonly ClockWarning[];
  readonly timeZone: string;
  /** Local date of today in `timeZone`. */
  readonly today: LocalDate;
  readonly continuousLimitSeconds: number;
}

/**
 * Shows the live warnings.
 *
 * @param props - Component properties.
 * @returns The list.
 */
export function WarningList(props: WarningListProps): JSX.Element | null {
  const { t, languageTag } = useI18n();

  if (props.warnings.length === 0) {
    return null;
  }

  const list = (
    <ul className={styles["list"]}>
      {props.warnings.map((warning) => {
        const described = describeWarning(
          warning,
          t,
          languageTag,
          props.timeZone,
          props.today,
          props.continuousLimitSeconds,
        );

        return (
          <li key={warning.kind}>
            <Alert tone={described.tone}>{described.text}</Alert>
          </li>
        );
      })}
    </ul>
  );

  return list;
}
